import type { SurveyAnswerValue } from '@coorum-research/shared-common/types'
import type { QuestionRatingModelData } from '@coorum-research/shared-common/types/survey-json.type'
import type { SurveyElementForm } from './survey-element-form'
import { useCallback, useState } from 'react'
import { cn } from '../../utils'
import { RatingStarIcon } from '../icons/RatingStarIcon'
import { useTheme } from '../ThemeContext'
import { ElementCommentField } from './ElementCommentField'
import { ElementHeader } from './ElementHeader'
import { ElementValidationMessage } from './ElementValidationMessage'
import { SurveyElementLayout } from './SurveyElementLayout'

type Props = {
  currentForm: SurveyElementForm
  validationError: string | null
  onValueChange?: (value: SurveyAnswerValue) => void
  onCommentChange?: (comment: string) => void
  isPanelQuestion?: boolean
  onClickNext?: () => void
}

export function SurveyElementRating({
  currentForm,
  validationError,
  onValueChange,
  onCommentChange,
  isPanelQuestion = false,
  onClickNext,
}: Props) {
  const themeJson = useTheme()
  const currentElement = currentForm.elementJson as QuestionRatingModelData
  const [value, setValue] = useState<number>(() => {
    const initialValue = currentForm.getValue() as number
    return initialValue || 0
  })

  const mainColor = themeJson.mainColor || '#FABE0A'

  // rating設定値
  const rateCount = currentElement.rateCount ?? 5
  const rateMin = currentElement.rateMin ?? 1
  const rateMax = currentElement.rateMax ?? rateCount
  const rateType = currentElement.rateType ?? 'stars'

  // 評価値の変更処理
  const handleRatingChange = useCallback((newValue: number) => {
    setValue(newValue)
    currentForm.setElementResultRecordValue(newValue)
    if (onValueChange) {
      // onValueChangeが定義の場合は、onClickNextを呼ばない。親componentでの処理に任せる。
      onValueChange(newValue)
    } else {
      if (!currentElement.showCommentArea) {
        onClickNext?.()
      }
    }
  }, [currentForm, onValueChange, onClickNext, currentElement.showCommentArea])

  // ユニークなname属性を生成
  const radioGroupName = `${currentElement.name}_rating`

  // 星評価の表示
  const renderStars = () => {
    return Array.from({ length: rateMax - rateMin + 1 }, (_, index) => {
      const i = rateMin + index
      const isChecked = i === value

      // 各星の状態を詳細に判定
      const isSelected = i <= value

      return (
        <label
          key={i}
          className="relative inline-flex cursor-pointer items-center justify-center rounded-sm px-0.5 py-1 outline-none transition-all duration-200 ease-in-out focus-within:outline-none focus:outline-none active:outline-none"
          title={String(i)}
        >
          <input
            type="radio"
            value={i}
            name={radioGroupName}
            checked={isChecked}
            onChange={() => handleRatingChange(i)}
            aria-required={currentElement.isRequired}
            aria-invalid={!!validationError}
            style={{
              position: 'absolute',
              left: '-10000px',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
              outline: 'none',
              border: 'none',
              boxShadow: 'none',
            }}
          />
          <div className="relative" style={{ width: '48px', height: '48px' }}>
            <RatingStarIcon
              filled={isSelected}
              color={mainColor}
              width={48}
              height={48}
              className="absolute inset-0"
            />
          </div>
        </label>
      )
    })
  }

  // ラベル評価の表示
  const renderLabels = () => {
    if (!currentElement.rateValues) {
      return Array.from({ length: rateMax - rateMin + 1 }, (_, index) => {
        const i = rateMin + index
        const isChecked = i === value

        return (
          <label
            key={i}
            className={cn(
              'cursor-pointer inline-flex items-center justify-center',
              'px-4 py-2 border rounded transition-all duration-200',
              'hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500',
              isChecked
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-white text-gray-700 border-gray-300',
            )}
            style={{
              color: isChecked ? 'white' : themeJson?.mainTextColor,
            }}
          >
            <input
              type="radio"
              value={i}
              name={radioGroupName}
              checked={isChecked}
              onChange={() => handleRatingChange(i)}
              aria-required={currentElement.isRequired}
              aria-invalid={!!validationError}
            />
            {i}
          </label>
        )
      })
    }

    // カスタムラベル
    return currentElement.rateValues.map((rateValue) => {
      const rateNum = typeof rateValue === 'number' ? rateValue : rateValue.value
      const rateText = typeof rateValue === 'number' ? String(rateValue) : rateValue.text
      const isChecked = rateNum === value

      return (
        <label
          key={rateNum}
          className={cn(
            'cursor-pointer inline-flex items-center justify-center',
            'px-4 py-2 border rounded transition-all duration-200',
            'hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500',
            isChecked
              ? 'bg-blue-500 text-white border-blue-500'
              : 'bg-white text-gray-700 border-gray-300',
          )}
          style={{
            color: isChecked ? 'white' : themeJson?.mainTextColor,
          }}
        >
          <input
            type="radio"
            value={rateNum}
            name={radioGroupName}
            checked={isChecked}
            onChange={() => handleRatingChange(rateNum)}
            aria-required={currentElement.isRequired}
            aria-invalid={!!validationError}
          />
          {rateText}
        </label>
      )
    })
  }

  return (
    <SurveyElementLayout isPanelQuestion={isPanelQuestion}>
      <ElementValidationMessage
        validationError={validationError}
      />
      <ElementHeader
        currentElement={currentElement}
      />

      <div className="!mt-0 h-full px-[2px] pb-[2px]" role="presentation">
        <div className="!w-full" data-rendered="r">
          <fieldset role="radiogroup" className="m-0 flex justify-center gap-2 border-0 p-0">
            {rateType === 'stars' ? renderStars() : renderLabels()}
          </fieldset>
        </div>
      </div>

      {currentElement.showCommentArea && (
        <ElementCommentField
          label={currentElement.commentText}
          placeholder={currentElement.commentPlaceholder}
          defaultValue={currentForm.getComment()}
          onChange={(e) => {
            currentForm.setElementResultRecordComment(e.target.value)
            onCommentChange?.(e.target.value)
          }}
        />
      )}
    </SurveyElementLayout>
  )
}
